import { ReactNode, useMemo, useRef } from "react";
import { Column, usePagination, useSortBy, useTable } from "react-table";

import { TablePagination } from "./DataTable";
import { LoadingOverlay } from "./LoadingComponents";
import { TableRenderer } from "./TableRenderer";

export default function DefaultTable<T extends object = any>({
  columns,
  data,
  empty,
  onRowClick,
  paginate = false,
  isLoading,
  isSortable,
  rowClassName,
  tableClassName,
}: {
  columns: Column<T>[];
  data: T[];
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
  paginate?: boolean;
  isLoading?: boolean;
  isSortable?: boolean;
  rowClassName?: (row: T) => string | undefined;
  tableClassName?: string;
}) {
  const {
    canNextPage,
    canPreviousPage,
    getTableBodyProps,
    getTableProps,
    gotoPage,
    headerGroups,
    page,
    pageCount,
    prepareRow,
    rows,
    state: { pageIndex },
  } = useTable<T>(
    {
      columns,
      data,
      initialState: {
        pageSize: 10,
      },
    },
    ...(useRef(isSortable).current ? [useSortBy] : []),
    usePagination,
  );

  const shownRows = useMemo(
    () => (paginate ? page : rows),
    [paginate, page, rows],
  );

  return (
    <div className="relative flex w-full flex-col">
      <div className="w-full">
        <TableRenderer
          getTableProps={getTableProps}
          getTableBodyProps={getTableBodyProps}
          headerGroups={headerGroups}
          rows={shownRows}
          prepareRow={prepareRow}
          columns={columns}
          empty={empty}
          onRowClick={onRowClick}
          isSortable={isSortable}
          rowClassName={rowClassName}
          tableClassName={tableClassName}
        />
        {paginate && (
          <TablePagination
            className="mt-3"
            pageIndex={pageIndex}
            pageCount={pageCount}
            canNextPage={canNextPage}
            canPreviousPage={canPreviousPage}
            onChangePage={gotoPage}
            totalRows={rows.length}
          />
        )}
      </div>
      {isLoading ? <LoadingOverlay /> : null}
    </div>
  );
}
